import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { usePools } from '../hooks/usePools';
import Loader from '../components/Loader';
import { POOL_LABELS, formatUSDTRaw } from '../utils/helpers';
import '../styles/pools.css';

export default function Pools({ onNotify }) {
  const { account, userId, provider } = useWeb3();
  const {
    getUserPoolEligibility, getUserTotalClaimable,
    claimPool, claimWeekly, claimMonthly, claimAll,
  } = usePools();

  const [eligibility, setEligibility] = useState(null);
  const [claimable, setClaimable] = useState(null);
  const [loading, setLoading] = useState(false);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    if (provider && userId) loadData();
    // eslint-disable-next-line
  }, [userId, provider]);

  async function loadData() {
    setLoading(true);
    try {
      const [elig, claim] = await Promise.all([
        getUserPoolEligibility(userId),
        getUserTotalClaimable(userId),
      ]);
      setEligibility(elig);
      setClaimable(claim);
    } catch {}
    setLoading(false);
  }

  async function handleClaimAll() {
    setClaiming(true);
    const result = await claimAll();
    if (result.success) {
      onNotify('All rewards claimed!', 'success');
      setTimeout(loadData, 2000);
    } else {
      onNotify(result.error, 'error');
    }
    setClaiming(false);
  }

  async function handleClaimPool(index) {
    setClaiming(true);
    const result = await claimPool(index);
    if (result.success) {
      onNotify(`${POOL_LABELS[index]} claimed!`, 'success');
      setTimeout(loadData, 2000);
    } else {
      onNotify(result.error, 'error');
    }
    setClaiming(false);
  }

  async function handleClaimWeekly() {
    setClaiming(true);
    const result = await claimWeekly();
    if (result.success) {
      onNotify('Weekly royalty claimed!', 'success');
      setTimeout(loadData, 2000);
    } else {
      onNotify(result.error, 'error');
    }
    setClaiming(false);
  }

  async function handleClaimMonthly() {
    setClaiming(true);
    const result = await claimMonthly();
    if (result.success) {
      onNotify('Monthly royalty claimed!', 'success');
      setTimeout(loadData, 2000);
    } else {
      onNotify(result.error, 'error');
    }
    setClaiming(false);
  }

  if (!account) {
    return (
      <div className="page-container">
        <div className="connect-prompt">
          <div className="connect-icon">&#x1F512;</div>
          <h2>Daily Share & Royalty</h2>
          <p>Connect your wallet to view rewards</p>
        </div>
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="page-container">
        <div className="connect-prompt">
          <h2>Not Registered</h2>
          <p>Go to Dashboard to register first</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="page-container"><Loader text="Loading rewards..." /></div>;
  }

  const grandTotal = claimable?.grandTotal;
  const hasClaimable = grandTotal && grandTotal.toString() !== '0';

  return (
    <div className="page-container">
      <h2 className="page-title">Daily Share & Royalty</h2>

      {/* ===== CLAIM ALL HERO ===== */}
      <div className="pool-hero glass-card animate-in">
        <div className="pool-hero-inner">
          <span className="pool-hero-label">Available Balance</span>
          <span className="pool-hero-value">${formatUSDTRaw(grandTotal)} <small>USDT</small></span>
        </div>
        <button
          className="btn-gradient btn-full"
          onClick={handleClaimAll}
          disabled={claiming || !hasClaimable}
        >
          {claiming ? <span className="btn-loading"><span className="btn-spinner"></span>Claiming...</span> : 'Claim All Rewards'}
        </button>
      </div>

      {/* ===== UPGRADE POOLS ===== */}
      <h3 className="pool-section-heading animate-in" style={{animationDelay:'0.05s'}}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20V10M18 20V4M6 20v-4"/></svg>
        Daily Share
      </h3>

      <div className="pool-income-list">
        {POOL_LABELS.map((label, i) => {
          const isEligible = eligibility?.pools?.[i];
          const poolAmt = claimable?.poolAmounts?.[i];
          const hasAmount = poolAmt && poolAmt.toString() !== '0';
          return (
            <div key={i} className={`pool-income-card glass-card animate-in ${isEligible ? 'eligible' : 'locked'}`} style={{animationDelay:`${0.05 + i * 0.04}s`}}>
              <div className="pic-left">
                <div className={`pic-indicator ${isEligible ? 'active' : ''}`}></div>
                <div className="pic-info">
                  <span className="pic-name">{label}</span>
                  <span className={`pic-badge ${isEligible ? 'eligible' : 'locked'}`}>
                    {isEligible ? 'Eligible' : 'Locked'}
                  </span>
                </div>
              </div>
              <div className="pic-right">
                <span className={`pic-amount ${hasAmount ? 'has-value' : ''}`}>
                  ${hasAmount ? formatUSDTRaw(poolAmt) : '0.00'}
                </span>
                {isEligible && hasAmount && (
                  <button className="pic-claim-btn" onClick={() => handleClaimPool(i)} disabled={claiming}>
                    Claim
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== ROYALTY ===== */}
      <h3 className="pool-section-heading animate-in" style={{animationDelay:'0.3s'}}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
        Royalty Income
      </h3>

      <div className="pool-income-list">
        {/* Weekly */}
        <div className={`pool-income-card glass-card animate-in ${eligibility?.weekly ? 'eligible' : 'locked'}`} style={{animationDelay:'0.34s'}}>
          <div className="pic-left">
            <div className={`pic-indicator weekly ${eligibility?.weekly ? 'active' : ''}`}></div>
            <div className="pic-info">
              <span className="pic-name">Weekly Royalty</span>
              <span className={`pic-badge ${eligibility?.weekly ? 'eligible' : 'locked'}`}>
                {eligibility?.weekly ? 'Eligible' : 'All 7 pkgs + 2 directs'}
              </span>
            </div>
          </div>
          <div className="pic-right">
            <span className={`pic-amount ${claimable?.weeklyAmount?.toString() !== '0' ? 'has-value' : ''}`}>
              ${claimable?.weeklyAmount ? formatUSDTRaw(claimable.weeklyAmount) : '0.00'}
            </span>
            {eligibility?.weekly && claimable?.weeklyAmount?.toString() !== '0' && (
              <button className="pic-claim-btn" onClick={handleClaimWeekly} disabled={claiming}>Claim</button>
            )}
          </div>
        </div>

        {/* Monthly */}
        <div className={`pool-income-card glass-card animate-in ${eligibility?.monthly ? 'eligible' : 'locked'}`} style={{animationDelay:'0.38s'}}>
          <div className="pic-left">
            <div className={`pic-indicator monthly ${eligibility?.monthly ? 'active' : ''}`}></div>
            <div className="pic-info">
              <span className="pic-name">Monthly Royalty</span>
              <span className={`pic-badge ${eligibility?.monthly ? 'eligible' : 'locked'}`}>
                {eligibility?.monthly ? 'Eligible' : 'All 7 pkgs + 3 directs'}
              </span>
            </div>
          </div>
          <div className="pic-right">
            <span className={`pic-amount ${claimable?.monthlyAmount?.toString() !== '0' ? 'has-value' : ''}`}>
              ${claimable?.monthlyAmount ? formatUSDTRaw(claimable.monthlyAmount) : '0.00'}
            </span>
            {eligibility?.monthly && claimable?.monthlyAmount?.toString() !== '0' && (
              <button className="pic-claim-btn" onClick={handleClaimMonthly} disabled={claiming}>Claim</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
